import React, { useContext, useState, useEffect, useLayoutEffect } from 'react'
import { Title } from '@fs/zion-head'
import {
  Row,
  Tab,
  Tabs,
  LayoutBand,
  Button,
  ZoomPanControlButton,
  useStatusOverlay,
  H1,
  H3,
  DataPreview,
  Subheading,
  Image,
  colors,
  IconButton,
  Overline,
  Toggle,
} from '@fs/zion-ui'
import { ContentCopy, NoticeLoading, ContentDownload, MenuNewWindow, ArrowCaret } from '@fs/zion-icon'
import zionDebug from '@fs/zion-debug'
import { useLocation, useQueryParams, Link } from '@fs/zion-router'
import getPermission from '@fs/zion-permissions'
import { css } from '@emotion/core'
import {
  StaticImageViewer,
  RibbonViewer,
  ComboViewer,
  RecordHighlight,
  HighlightsBase,
  useHighlights,
} from '@fs/zion-image-viewer'
import { getSessionId } from '@fs/zion-session'
import { useFeatureFlag } from '@fs/zion-flags'
import { GridStart, ResponsiveProvider } from '@fs/zion-ui/quarks-authorized-use-only'
import { useTranslation } from 'react-i18next'
import Separator from '@fs/zion-ui/dist/cjs/separator'
import {
  getReferencedImageArksToDisplayPersons,
  getHighlights,
  getImage,
  requestSLSActions,
  requestSLSImageData,
  useSls,
  useSlsActions,
  useSlsDispatchContext,
  useSlsStateContext,
  useSlsTypes,
} from '@fs/zion-sls'
import { getImages } from '../../../shared/services/rms/rmsService'
import { headers } from '../../../shared/services/config'
import ElementTreeView from './components/elementTreeView/ElementTreeView'
import HistoryTableView from './components/historyTableView/HistoryTableView'
import { getImageArkUrl, getImageThumbUrl } from '../../../shared/services/das/dasService'
import eliteDatumLogo from './images/eliteDatumLogo.png'
import Holiday from './images/holiday'
import { setDisplayHighlights } from '../../utils/imageHighlightUtils'
import DebugErrorNotice from './components/DebugErrorNotice'
import DarkModeControl from '../../../shared/components/darkModeControl/DarkModeControl'
import { DarkModeContext } from '../../../shared/providers/darkModeProvider/DarkModeProvider'

const debug = zionDebug('rip:debug:DebugViewer')

const viewerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minWidth: 0,
}

const fsUserAgentChain = 'Elite Datum'

function download(filename, text) {
  const a = document.createElement('a')
  a.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(`\uFEFF${text}`)}`)
  a.setAttribute('download', filename)
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

async function downloadActions(partitionKey) {
  const actions = await requestSLSActions({ partitionKey, fsUserAgentChain })
  const text = JSON.stringify(actions, null, 2)
  download(`${partitionKey.replace(/:/g, '_') || 'unknown-ark'}-actions.json`, text)
}

async function downloadElements(partitionKey) {
  const elements = await requestSLSImageData({ partitionKey, fsUserAgentChain })
  const text = JSON.stringify(elements, null, 2)
  download(`${partitionKey.replace(/:/g, '_') || 'unknown-ark'}.json`, text)
}

const notAuthorizedCss = css`
  padding: 80px 20px;
`

const wrapperCss = css`
  &,
  [data-reach-tabs],
  [data-reach-tab-panels],
  [data-reach-tab-panel]:not([hidden]) {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
`

const containerCss = css`
  display: flex;
  flex-direction: column;
  position: relative;
  flex-grow: 1;
  height: 100%;
`
const imageWrapperCss = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

export default function DebugViewer() {
  const { theme, useAllowDarkMode } = useContext(DarkModeContext)
  const [displayHighlights, displayHighlightsApi] = useHighlights()
  const shared_dasProxy = useFeatureFlag('shared_dasProxy')
  const showJiapuDataFlag = useFeatureFlag('rip_sharedImageViewer_showJiapu')

  const [t] = useTranslation()

  // NOTE: this is to get rid of double scrollbars
  useLayoutEffect(() => {
    const main = document.querySelector('main')
    if (main && !main.style.height) {
      main.style.height = 'calc(100vh - 64px)'
    }
  }, [])
  const { query } = useQueryParams()
  const location = useLocation()
  useAllowDarkMode()

  const { lang, elementId } = query
  const idx = location.pathname.lastIndexOf('/')
  const imageArk = decodeURIComponent(location.pathname.substring(idx + 1))
  const [imageArks, setImageArks] = useState([])
  const [imageArksInGroup, setImageArksInGroup] = useState([])

  const { data: dataTypes } = useSlsTypes({
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    dispatchTypes: true,
    lang: 'en',
  })

  const {
    loading: loadingSls,
    error: errorSls,
    data,
  } = useSls({
    imageArks,
    fsUserAgentChain,
    lang,
    dispatch: true,
    sort: true,
    enableHook: true,
    types: dataTypes,
    skipProcessingLargeData: !showJiapuDataFlag.isOn,
  })
  const {
    loading: loadingActions,
    error: errorActions,
    data: actionDatums,
  } = useSlsActions({
    imageArks: [imageArk],
    fsUserAgentChain,
  })

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authorized, setAuthorized] = useState(false)
  const [loadingAuthorized, setLoadingAuthorized] = useState(true)

  const { columnCount: initialColumnCount, images } = {
    columnCount: 6,
    images: imageArksInGroup?.length
      ? imageArksInGroup?.map((key) => ({
          id: `image-${key}`,
          alt: `Image ${key + 1}`,
          src: getImageArkUrl(key, shared_dasProxy.isOn),
          ribbonSrc: getImageThumbUrl(key, shared_dasProxy.isOn),
          ark: key,
        }))
      : [
          {
            id: `image-${imageArk}`,
            alt: `Image ${imageArk + 1}`,
            src: getImageArkUrl(imageArk, shared_dasProxy.isOn),
            ribbonSrc: getImageThumbUrl(imageArk, shared_dasProxy.isOn),
            ark: imageArk,
          },
        ],
  }

  const [title, setTitle] = useState(imageArk)
  const [prevImageArk, setPrevImageArk] = useState()
  const [nextImageArk, setNextImageArk] = useState()
  const [focusedIndex, setFocusedIndex] = useState(0)
  const [currentImage, setCurrentImage] = useState()
  const [columnCount, setColumnCount] = useState(initialColumnCount)
  const [showImageGrid, setShowImageGrid] = useState(false)
  const [holidayOverlay, setHolidayOverlay] = useState(false)
  const ribbonViewerImages = images?.map((image) => {
    return { id: image.id, alt: image.alt, src: image.ribbonSrc }
  })
  const dispatch = useSlsDispatchContext()
  const state = useSlsStateContext()
  useEffect(() => {
    if (currentImage) {
      const currentHighlights = getHighlights({ element: currentImage })
      setDisplayHighlights(currentHighlights, displayHighlightsApi)
    }
  }, [currentImage, displayHighlightsApi])

  useEffect(() => {
    const fetchPermission = async () => {
      const [eliteDatum, recordsManager] = await Promise.all([
        getPermission('RIP-Perm-EliteDatum', 'RIP-Ctx-GeneralImageOps'),
        getPermission('RIP-Perm-RecordsManager', 'RIP-Ctx-RecordsManager'),
      ])
      setAuthorized(eliteDatum.authorized || recordsManager.authorized)
      setLoadingAuthorized(false)
    }
    fetchPermission()
  }, [])

  const environment = {
    integ: 'integration',
    prod: 'www',
    beta: 'beta',
  }[getSessionId()?.split('-')[(getSessionId()?.split('-').length || 0) - 1]]

  useEffect(() => {
    if (!loadingSls && !loadingActions && !errorSls) {
      dispatch({
        type: 'BATCH_DISPATCHES',
        value: [
          {
            type: 'SET_DATA',
            value: {
              currentImageArk: imageArk,
            },
          },
          { type: 'SET_ACTIONS', value: actionDatums },
        ],
      })
    }
  }, [
    dispatch,
    loadingSls,
    errorSls,
    data.slsData?.imageDatums,
    data.slsData?.elements,
    data.types,
    loadingActions,
    actionDatums,
    imageArk,
  ])

  useEffect(() => {
    if (errorSls) setError(errorSls)
    if (errorActions) setError(errorActions)
  }, [errorActions, errorSls])

  useEffect(() => {
    if ((!loadingSls && !loadingActions) || error) setLoading(false)
  }, [error, loadingActions, loadingSls])

  useEffect(() => {
    setImageArks([imageArk])

    getImages(imageArk)
      .then((allImageArks) => {
        if (!allImageArks) throw new Error('Image Arks Are Null')
        const foundIndex = allImageArks?.findIndex((ark) => ark === imageArk)
        if (foundIndex) setFocusedIndex(foundIndex)
        const prevImageArkToSet = foundIndex - 1 >= 0 ? allImageArks[foundIndex - 1] : null
        const nextImageArkToSet =
          foundIndex + 1 <= (allImageArks?.length || 0) - 1 ? allImageArks[foundIndex + 1] : null
        setPrevImageArk(prevImageArkToSet)
        setNextImageArk(nextImageArkToSet)
        setImageArksInGroup(allImageArks)
      })
      .catch((e) => {
        setError(new Error(`Unable to load group image data. ${e.message}`))
      })
  }, [imageArk])

  useEffect(() => {
    if (!loading && currentImage && state?.images) {
      const additionalImageArks = getReferencedImageArksToDisplayPersons(currentImage, imageArks)
      if (additionalImageArks.length && additionalImageArks.every((ark) => !imageArks.includes(ark))) {
        setImageArks([...imageArks, ...additionalImageArks])
      }
    }
  }, [currentImage, loading, imageArks, state?.images])

  useEffect(() => {
    if (state.images) setCurrentImage(getImage(state.images, state.currentImageArk))
  }, [state.currentImageArk, state.images])

  const showStatusOverlay = useStatusOverlay()

  const handleChange = ({ target }) => setHolidayOverlay(target.checked)

  debug('Render: ', {
    zionSlsState: state,
    loading,
    loadingSls,
    loadingActions,
    error,
    errorSls,
    errorActions,
    data,
    actionDatums,
    imageArks,
    focusedIndex,
    images,
  })
  if (!authorized && !loadingAuthorized) {
    return (
      <div css={notAuthorizedCss}>
        <H1 centered size="H3">
          {t('debug-viewer.not-authorized', 'Not authorized')}
          <Subheading>
            Please contact the RIP (Patrick Liechty or Nathan Tanner) team via #rip-users channel on Slack to request
            access.
          </Subheading>
        </H1>
      </div>
    )
  }
  return (
    <ResponsiveProvider css={wrapperCss}>
      <Title breadcrumbs={title}>Elite Datums</Title>
      {error && <DebugErrorNotice errorLabel="Error loading image data" error={error} />}
      <LayoutBand color={colors.green30} marginY="nano" marginX="nano" maxWidth="xxl">
        {holidayOverlay && (
          <div
            style={{
              backgroundColor: 'red',
              backgroundImage:
                'linear-gradient(45deg, white 25%, transparent 25.5%, transparent 50%, white 50.5%, white 75%, transparent 75.5%, transparent)',
              backgroundSize: '20px 20px',
              padding: '20px',
            }}
          >
            <div style={{ backgroundColor: 'white' }}>
              <Row alignY="middle" alignX="start" wrap>
                <Image
                  src={eliteDatumLogo}
                  alt="Elite Datum Logo"
                  maxWidth="175px"
                  title="Elite Datum was forged as a name to command respect, embodying the formidable power of this product. It signals not only the unparalleled precision and unique capabilities of the system but also its vast capacity to process and dominate data at scale."
                />
                <H3 growX>
                  {title}
                  <Overline>Elite Datum: SLS Debugger</Overline>
                  <Subheading>
                    <Link
                      external
                      wayfinding
                      to={`/ark:/61903/${imageArk}?view=index`}
                      onClick={(e) => {
                        e.preventDefault()
                        window.open(`/ark:/61903/${imageArk}?view=index`, '_blank')
                      }}
                    >
                      {`/ark:/61903/${imageArk}?view=index`}
                    </Link>
                  </Subheading>
                </H3>
              </Row>
            </div>
          </div>
        )}
        {!holidayOverlay && (
          <Row alignY="middle" alignX="start" wrap>
            <Image
              src={eliteDatumLogo}
              alt="Elite Datum Logo"
              maxWidth="175px"
              title="Elite Datum was forged as a name to command respect, embodying the formidable power of this product. It signals not only the unparalleled precision and unique capabilities of the system but also its vast capacity to process and dominate data at scale."
            />
            <H3 growX>
              {title}
              <Overline>Elite Datum: SLS Debugger</Overline>
              <Subheading>
                <Link
                  external
                  wayfinding
                  to={`/ark:/61903/${imageArk}?view=index`}
                  onClick={(e) => {
                    e.preventDefault()
                    window.open(`/ark:/61903/${imageArk}?view=index`, '_blank')
                  }}
                >
                  {`/ark:/61903/${imageArk}?view=index`}
                </Link>
              </Subheading>
            </H3>
          </Row>
        )}
      </LayoutBand>
      <LayoutBand marginY="nano" color={colors.gray03} marginX="nano" maxWidth="xxl">
        <Row alignX="center" gutters="sm">
          <Row alignX="start" gutters="sm">
            <Button
              data-testid="debug-viewer-previous-image"
              disabled={!prevImageArk}
              external
              to={prevImageArk && `/ark:/61903/${prevImageArk}?view=debug`}
              Icon={ArrowCaret}
              iconDirection="backward"
              title={prevImageArk}
            >
              Previous Image
            </Button>
            <Button
              data-testid="debug-viewer-next-image"
              disabled={!nextImageArk}
              external
              to={nextImageArk && `/ark:/61903/${nextImageArk}?view=debug`}
              Icon={ArrowCaret}
              iconAtEnd
              title={nextImageArk}
            >
              Next Image
            </Button>
          </Row>
          <Separator growX />
          <Row alignX="end" gutters="sm">
            <IconButton
              Icon={ContentDownload}
              title="Download Actions JSON"
              onClick={() => downloadActions(imageArk)}
            />
            <IconButton
              Icon={ContentDownload}
              title="Download Elements JSON"
              onClick={() => downloadElements(imageArk)}
            />
            <IconButton
              Icon={MenuNewWindow}
              title="Image ARK Search Results"
              target="_blank"
              external
              to={`https://${environment}.familysearch.org/records/images/search-results?ark=${title}`}
            />
            <Toggle title="Holiday Overlay" name="holiday" checked={holidayOverlay} onChange={handleChange} />
          </Row>
        </Row>
      </LayoutBand>
      <DataPreview.Provider loading={loading} delay={100}>
        <DataPreview.Loading>
          <Row alignX="center">
            <NoticeLoading size="lg" />
          </Row>
        </DataPreview.Loading>

        <DataPreview.Ready style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Tabs defaultIndex={query.mode === 'table' ? 2 : 0}>
            <Tab title="Element Tree" data-testid="debug-viewer-tree-tab">
              <LayoutBand marginY="nano">
                <ElementTreeView fsUserAgentChain={fsUserAgentChain} initialExpandElementId={elementId} />
              </LayoutBand>
            </Tab>
            <Tab title="Image Viewer" data-testid="debug-viewer-image-viewer-tab">
              <GridStart
                style={{
                  maxWidth: 1920,
                  margin: '0 auto',
                  flexGrow: 1,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div css={containerCss}>
                  <section
                    css={imageWrapperCss}
                    data-theme={theme}
                    aria-label={t('film-viewer.accessibility-heading', 'Image Viewer')}
                    style={viewerContainerStyle}
                  >
                    <ComboViewer
                      showImageGrid={showImageGrid}
                      setShowImageGrid={setShowImageGrid}
                      columnCount={columnCount}
                      setColumnCount={setColumnCount}
                      focusedIndex={focusedIndex}
                      setFocusedIndex={setFocusedIndex}
                      ImageComponent={StaticImageViewer}
                      images={images}
                      adjustmentControls={<DarkModeControl />}
                      thinganated
                      controls={
                        <ZoomPanControlButton
                          Icon={ContentCopy}
                          onClick={() => {
                            navigator.clipboard.writeText(images?.[focusedIndex]?.ark).then(
                              () => showStatusOverlay({ message: 'Copied image ARK to clipboard', type: 'success' }),
                              () =>
                                showStatusOverlay({
                                  message: 'Failed to copy image ARK to clipboard',
                                  type: 'error',
                                })
                            )
                          }}
                          label="Copy ARK to clipboard"
                          expanded
                        />
                      }
                    >
                      <HighlightsBase state={displayHighlights} Component={RecordHighlight} />
                    </ComboViewer>
                  </section>
                </div>
                {!showImageGrid && (
                  <RibbonViewer
                    focusedIndex={focusedIndex}
                    setFocusedIndex={setFocusedIndex}
                    images={ribbonViewerImages}
                  />
                )}
              </GridStart>
            </Tab>
            <Tab title="History Table" data-testid="debug-viewer-history-tab">
              <LayoutBand marginY="nano">
                <HistoryTableView
                  actions={currentImage?.actions}
                  arkPath={imageArk}
                  title={title}
                  changeTitle={setTitle}
                />
              </LayoutBand>
            </Tab>
          </Tabs>
        </DataPreview.Ready>
      </DataPreview.Provider>
      {holidayOverlay && <Holiday />}
    </ResponsiveProvider>
  )
}
